import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { ArrowLeft, Save } from 'lucide-react';
import { cctvApi } from '@/api/cctv';
import { clientsApi } from '@/api/clients';
import { cctvSchema, type CCTVFormData } from '@/utils/validators';
import type { Client } from '@/types';
import FormField from '@/components/FormField';
import LoadingSpinner from '@/components/LoadingSpinner';

export function CCTVForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const { data: clientsResponse } = useQuery({
    queryKey: ['clients', 'all'],
    queryFn: () => clientsApi.getAll({ limit: 100 }),
  });

  const { data: cctvResponse, isLoading: loadingCCTV } = useQuery({
    queryKey: ['cctv', id],
    queryFn: () => cctvApi.getById(id!),
    enabled: isEditing,
  });

  const clients: Client[] = clientsResponse?.data?.data || [];

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CCTVFormData>({
    resolver: zodResolver(cctvSchema),
    defaultValues: {
      clientId: '',
      brand: '',
      model: '',
      serialNumber: '',
      location: '',
      ipAddress: '',
      port: undefined,
      installationDate: '',
      status: 'active',
      notes: '',
    },
  });

  useEffect(() => {
    if (isEditing && cctvResponse?.data?.data) {
      const cctv = cctvResponse.data.data as any;
      reset({
        clientId: cctv.clientId || '',
        brand: cctv.brand || '',
        model: cctv.model || '',
        serialNumber: cctv.serialNumber || '',
        location: cctv.location || '',
        ipAddress: cctv.ipAddress || '',
        port: cctv.port ?? undefined,
        installationDate: cctv.installationDate ? cctv.installationDate.split('T')[0] : '',
        status: cctv.status || 'active',
        notes: cctv.notes || '',
      });
    }
  }, [isEditing, cctvResponse, reset]);

  const createMutation = useMutation({
    mutationFn: (data: CCTVFormData) => cctvApi.create(data as any),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['cctv'] });
      toast.success('Equipo CCTV creado exitosamente');
      navigate(`/cctv/${res.data.data.id}`);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Error al crear equipo CCTV');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: CCTVFormData) => cctvApi.update(id!, data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cctv'] });
      queryClient.invalidateQueries({ queryKey: ['cctv', id] });
      toast.success('Equipo CCTV actualizado exitosamente');
      navigate('/cctv');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Error al actualizar equipo CCTV');
    },
  });

  const onSubmit = async (data: CCTVFormData) => {
    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  if (isEditing && loadingCCTV) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="rounded-md p-2 text-surface-400 hover:bg-surface-100 hover:text-surface-600 transition-colors"
          aria-label="Volver"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-2xl font-bold text-surface-900">
          {isEditing ? 'Editar Equipo CCTV' : 'Nuevo Equipo CCTV'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="rounded-lg border border-surface-200 bg-white p-6 shadow-sm space-y-5">
          <h2 className="text-lg font-semibold text-surface-900">Información del Equipo</h2>

          <div className="grid gap-5 sm:grid-cols-2">
            <FormField label="Cliente" error={errors.clientId?.message} required>
              <select
                {...register('clientId')}
                className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="">Seleccionar cliente...</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </FormField>

            <FormField label="Estado" error={errors.status?.message} required>
              <select
                {...register('status')}
                className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
                <option value="maintenance">Mantenimiento</option>
              </select>
            </FormField>

            <FormField label="Marca" error={errors.brand?.message} required>
              <input
                type="text"
                {...register('brand')}
                className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm placeholder-surface-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="Ej: Hikvision"
              />
            </FormField>

            <FormField label="Modelo" error={errors.model?.message}>
              <input
                type="text"
                {...register('model')}
                className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm placeholder-surface-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="Ej: DS-2CD2T47G2-L"
              />
            </FormField>

            <FormField label="Número de Serie" error={errors.serialNumber?.message}>
              <input
                type="text"
                {...register('serialNumber')}
                className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm placeholder-surface-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="Número de serie"
              />
            </FormField>

            <FormField label="Ubicación" error={errors.location?.message}>
              <input
                type="text"
                {...register('location')}
                className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm placeholder-surface-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="Ej: Edificio A, Piso 3"
              />
            </FormField>

            <FormField label="Dirección IP" error={errors.ipAddress?.message}>
              <input
                type="text"
                {...register('ipAddress')}
                className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm placeholder-surface-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="Ej: 192.168.1.100"
              />
            </FormField>

            <FormField label="Puerto" error={errors.port?.message}>
              <input
                type="number"
                {...register('port', { valueAsNumber: true })}
                className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm placeholder-surface-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="Ej: 554"
              />
            </FormField>

            <FormField label="Fecha de Instalación" error={errors.installationDate?.message}>
              <input
                type="date"
                {...register('installationDate')}
                className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </FormField>
          </div>

          <FormField label="Notas" error={errors.notes?.message}>
            <textarea
              {...register('notes')}
              rows={3}
              className="block w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm placeholder-surface-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="Notas adicionales..."
            />
          </FormField>
        </div>

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-md border border-surface-300 bg-white px-4 py-2 text-sm font-medium text-surface-700 shadow-sm hover:bg-surface-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
          >
            {isSubmitting ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                {isEditing ? 'Actualizando...' : 'Guardando...'}
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                {isEditing ? 'Actualizar' : 'Guardar'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CCTVForm;
